import { useState } from "react";
import { Seo } from "../components/Seo";
import { motion } from "framer-motion";
import { CheckCircle2, Upload, X, FileText, Wrench } from "lucide-react";
import { toast } from "../components/ui/sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CATEGORIES, BUDGET_RANGES, CUSTOM_ORDER_PROCESS } from "../lib/constants";

const initialState = {
  name: "", email: "", phone: "", company: "", part_name: "", category: "",
  quantity: "1", target_vehicle_equipment: "", material: "", dimensions: "",
  budget_range: "", description: "", attachments: [],
};

export default function CustomOrdersPage() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const fd = new FormData();
          fd.append("file", file);
          const res = await api.post("/custom-orders/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          return { url: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`, name: file.name };
        })
      );
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} attached`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/custom-orders", { ...form, quantity: parseInt(form.quantity, 10) || 1, attachments: form.attachments.map((a) => a.url) });
      setSubmitted(true);
      toast.success("Custom order request submitted!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="custom-order-success-state">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <CheckCircle2 className="h-14 w-14 text-amber-light mx-auto mb-6" />
          <h1 className="font-display text-3xl uppercase text-white mb-3">Design Received</h1>
          <p className="text-zinc-400">Our engineers will review your specs and follow up at {form.email} within 1-2 business days with a quote and lead time.</p>
          <Button onClick={() => { setForm(initialState); setSubmitted(false); }} className="mt-8 bg-amber-light text-obsidian hover:bg-white" data-testid="custom-order-submit-another-btn">
            Submit Another Request
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-24" data-testid="custom-orders-page">
      <Seo
        title="Custom Part Fabrication | Orion Services LLC"
        description="Order custom-fabricated automotive, truck and tractor parts from Orion Services LLC. Submit your CAD file, sketch, or sample part for a precision-machined replacement."
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-light">No Part Off The Shelf?</span>
            <h1 className="font-display text-4xl sm:text-5xl uppercase text-white mt-2 mb-5">Custom Part Fabrication</h1>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Discontinued tractor models, one-off fleet modifications, vintage restorations — if it doesn't exist on a shelf, our engineers
              can reverse-engineer, model, and fabricate it. Submit your design below and we'll follow up with a firm quote.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-6" data-testid="custom-orders-intro-copy">
              Not every part has a catalog number. A 1970s Case tractor axle, a modified PTO coupler for a fabricated
              implement, or a bolt pattern that hasn't been manufactured in decades — these are the jobs our custom
              fabrication team handles daily. We work directly from whatever you can provide: a worn original part, a
              hand sketch with rough dimensions, a phone photo with a tape measure in frame, or a full CAD file if
              your shop already has one. There's no minimum order size, and no design is too small or too unusual
              to quote.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-10">
              Material capability covers mild and stainless steel, aluminum, bronze, and engineered polymers, machined
              on CNC equipment with pressure and stress testing before anything ships. We regularly fabricate for
              long-haul freight fleets, agricultural operations running legacy John Deere and Case IH equipment, and
              private owners restoring vintage trucks and tractors where OEM replacement parts are no longer produced.
              Every prototype gets a fitment check before we commit to a full production run, so you're never paying
              for a part that doesn't seat correctly the first time.
            </p>

            <div className="space-y-8">
              {CUSTOM_ORDER_PROCESS.map((step) => (
                <div key={step.number} className="flex gap-5" data-testid={`custom-order-process-step-${step.number}`}>
                  <span className="font-display text-3xl text-amber-500/30 shrink-0">{step.number}</span>
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-amber-100">{step.title}</h3>
                    <p className="text-sm text-zinc-500 mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 border border-amber-500/15 rounded-md p-6 sm:p-8 bg-carbon" data-testid="custom-order-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Full Name *</Label>
                <Input required data-testid="custom-order-form-name" value={form.name} onChange={handleChange("name")} className="!bg-obsidian border-amber-500/15" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Email *</Label>
                <Input required type="email" data-testid="custom-order-form-email" value={form.email} onChange={handleChange("email")} className="!bg-obsidian border-amber-500/15" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Phone *</Label>
                <Input required data-testid="custom-order-form-phone" value={form.phone} onChange={handleChange("phone")} className="!bg-obsidian border-amber-500/15" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Company (Optional)</Label>
                <Input data-testid="custom-order-form-company" value={form.company} onChange={handleChange("company")} className="!bg-obsidian border-amber-500/15" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Part Name / Description *</Label>
                <Input required placeholder="e.g. Custom PTO shaft coupler" data-testid="custom-order-form-part-name" value={form.part_name} onChange={handleChange("part_name")} className="!bg-obsidian border-amber-500/15" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="custom-order-form-category" className="!bg-obsidian border-amber-500/15">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="Other">Other / Not Listed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Target Vehicle / Equipment *</Label>
              <Input required placeholder="e.g. John Deere 4020, 1998 Peterbilt 379" data-testid="custom-order-form-vehicle" value={form.target_vehicle_equipment} onChange={handleChange("target_vehicle_equipment")} className="!bg-obsidian border-amber-500/15" />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Quantity</Label>
                <Input type="number" min="1" data-testid="custom-order-form-quantity" value={form.quantity} onChange={handleChange("quantity")} className="!bg-obsidian border-amber-500/15" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Material (Optional)</Label>
                <Input placeholder="e.g. Stainless steel" data-testid="custom-order-form-material" value={form.material} onChange={handleChange("material")} className="!bg-obsidian border-amber-500/15" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Dimensions (Optional)</Label>
                <Input placeholder="e.g. 45mm bore" data-testid="custom-order-form-dimensions" value={form.dimensions} onChange={handleChange("dimensions")} className="!bg-obsidian border-amber-500/15" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Budget Range (Optional)</Label>
              <Select value={form.budget_range} onValueChange={(v) => setForm((f) => ({ ...f, budget_range: v }))}>
                <SelectTrigger data-testid="custom-order-form-budget" className="!bg-obsidian border-amber-500/15">
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_RANGES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Specifications & Requirements *</Label>
              <Textarea required rows={4} placeholder="Describe the part, its function, tolerances, and any finish requirements" data-testid="custom-order-form-description" value={form.description} onChange={handleChange("description")} className="!bg-obsidian border-amber-500/15" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Design Files / Reference Photos (Optional)</Label>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {form.attachments.map((a, idx) => (
                  <div key={idx} className="relative flex items-center gap-1.5 bg-obsidian border border-amber-500/20 rounded px-2.5 py-1.5" data-testid={`custom-order-form-attachment-${idx}`}>
                    <FileText className="h-3.5 w-3.5 text-amber-light" />
                    <span className="text-xs text-zinc-300 max-w-[120px] truncate">{a.name}</span>
                    <button type="button" onClick={() => removeAttachment(idx)} data-testid={`custom-order-form-attachment-remove-${idx}`} className="text-zinc-500 hover:text-rose-400">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="flex items-center gap-2 text-xs font-mono uppercase text-zinc-300 border border-amber-500/20 rounded px-3 py-2 cursor-pointer hover:border-amber-500/50 hover:text-amber-light">
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? "Uploading..." : "Attach Files"}
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.dwg,.dxf,.step,.stp" data-testid="custom-order-form-file-upload" onChange={handleFileUpload} disabled={uploading} className="hidden" />
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-2">CAD files (STEP/DWG/DXF), PDFs, or photos. Max 15MB per file.</p>
            </div>
            <Button type="submit" disabled={submitting || uploading} data-testid="custom-order-form-submit" className="w-full bg-amber-light text-obsidian hover:bg-white font-semibold uppercase tracking-wide py-6">
              <Wrench className="h-4 w-4 mr-2" /> {submitting ? "Submitting..." : "Submit Custom Order"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
